import { api } from "../../../../api";
import {
  ArticleCreateProps,
  BillArticleProps,
  BillArticleResponseProps,
  BillCreateProps,
  BillDraftProps,
  BillItemProps,
  BillResponseProps,
  CustomCreateProps,
  CustomsProps,
  Notes,
  PackingPropsResponse,
  ProtocolsListProps,
  ProtocolsListResponse,
  ProtocolsUpdateProps,
  RadiometricsProps,
  RadiometricsResponse,
  RadiometricsUpdateProps,
  AggregatedPackingPropsResponse,
} from "./types";

export async function getCustomsGeneralApi(id: number): Promise<CustomsProps> {
  const { data } = await api.get<CustomsProps>(
    `/api/shipping-cases/${id}/customs-info`,
  );
  return data;
}

export async function createCustomsGeneralApi(
  id: number,
  params: Partial<CustomCreateProps>,
): Promise<CustomsProps> {
  const { data } = await api.put<CustomsProps>(
    `/api/shipping-cases/${id}/customs-info`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateCustomsGeneralApi(
  id: number,
  params: Partial<CustomCreateProps>,
): Promise<CustomsProps> {
  const { data } = await api.put<CustomsProps>(
    `/api/shipping-cases/${id}/customs-info`,
    {
      ...params,
    },
  );
  return data;
}

export async function getBulkRadiometricsApi(
  id: number,
  params: any,
): Promise<RadiometricsResponse> {
  const { data } = await api.get<RadiometricsResponse>(
    `/api/shipping-cases/${id}/bill-of-lading/radiometric-checks`,
    { params },
  );
  return data;
}

export async function getContainerRadiometricsApi(
  id: number,
  params: any,
): Promise<RadiometricsResponse> {
  const { data } = await api.get<RadiometricsResponse>(
    `/api/shipping-cases/${id}/bl-containers/radiometric-checks`,
    { params },
  );
  return data;
}

export async function updateRadiometricsApi(
  id: number,
  params: Partial<RadiometricsProps>,
): Promise<RadiometricsUpdateProps> {
  const { data } = await api.put<RadiometricsUpdateProps>(
    `/api/bill-of-lading/${id}/radiometric-checks`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateCustomsNotesApi(
  id: number,
  params: Partial<Notes>,
): Promise<void> {
  await api.patch<void>(`/api/shipping-cases/${id}/notes`, {
    ...params,
  });
}

export async function getBillListApi(
  id: number,
  params: any,
): Promise<BillResponseProps> {
  const { data } = await api.get<BillResponseProps>(
    `/api/shipping-cases/${id}/customs-bills`,
    { params },
  );
  return data;
}

export async function cancelCustomsBillApi(id: number): Promise<void> {
  await api.post<void>(`/api/customs-bill/${id}/cancel`);
}

export async function createBillGeneralApi(
  id: number,
  params: Partial<BillCreateProps>,
): Promise<BillItemProps> {
  const { data } = await api.post<BillItemProps>(
    `/api/shipping-cases/${id}/customs-bills`,
    {
      ...params,
    },
  );
  return data;
}

export async function getBillGeneralApi(
  id: number,
  bill_draft: string,
  bill_type: string,
): Promise<{
  bill: BillItemProps;
  articles: Record<string, BillArticleProps>;
  missing_hs_codes: boolean;
  dpo_number: string | null;
}> {
  const { data } = await api.get<{
    bill: BillItemProps;
    articles: Record<string, BillArticleProps>;
    missing_hs_codes: boolean;
    dpo_number: string | null;
  }>(`/api/shipping-cases/${id}/customs-bill-overview`, {
    params: { customs_bill_draft: bill_draft, customs_bill_type: bill_type },
  });
  return {
    bill: data.bill,
    articles: data.articles,
    missing_hs_codes: data.missing_hs_codes,
    dpo_number: data.dpo_number,
  };
}

export async function updateBillGeneralApi(
  id: number,
  params: Partial<BillCreateProps>,
): Promise<BillItemProps> {
  const { data } = await api.put<BillItemProps>(`/api/customs-bill/${id}`, {
    ...params,
  });
  return data;
}

export async function updateArticleApi(
  id: number,
  params: Partial<ArticleCreateProps>,
): Promise<BillArticleProps> {
  const { data } = await api.put<BillArticleProps>(
    `/api/customs-article/${id}`,
    {
      ...params,
    },
  );
  return data;
}

export async function getBillDetailsApi(id: number): Promise<BillItemProps> {
  const { data } = await api.get<BillItemProps>(`/api/customs-bill/${id}`);
  return data;
}

export async function getBillArticlesApi(
  scId: number,
  id: number,
): Promise<BillArticleResponseProps> {
  const { data } = await api.get<BillArticleResponseProps>(
    `/api/shipping-cases/${scId}/customs-articles?customs_bill_id=${id}`,
  );
  return data;
}

export async function createBillArticlesApi(
  id: number,
  params: Partial<ArticleCreateProps>,
): Promise<BillArticleProps> {
  const { data } = await api.post<BillArticleProps>(
    `/api/customs-bill/${id}/articles`,
    {
      ...params,
    },
  );
  return data;
}

export async function removeArticleApi(id: number): Promise<void> {
  await api.delete<void>(`/api/customs-article/${id}`);
}

export async function getPackingShortApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<PackingPropsResponse> {
  const { data } = await api.get<PackingPropsResponse>(
    `/api/shipping-cases/${id}/packing-lists/short`,
    { params },
  );
  return data;
}

export async function getAggregatedPackingShortApi(
  id: number,
  params: Record<string, any>,
): Promise<AggregatedPackingPropsResponse> {
  const { data } = await api.get<AggregatedPackingPropsResponse>(
    `/api/shipping-cases/${id}/packing-lists/aggregated`,
    { params },
  );
  return data;
}

export async function getBillDraftApi(
  id: number,
  params: { customs_bill_type: string },
): Promise<BillDraftProps[]> {
  const { data } = await api.get<BillDraftProps[]>(
    `/api/shipping-cases/${id}/packing-lists/customs-bill-drafts`,
    { params },
  );
  return data;
}

export async function updatePackingListApi(
  id: number,
  params: {
    mrn_code: string;
    packing_list_ids: number[];
  },
): Promise<string> {
  const { data } = await api.post<string>(`/api/customs-bill/${id}/clear`, {
    ...params,
  });
  return data;
}

export async function exportCustomsBillApi(id: number): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.get(`/api/customs-article/${id}/export/pdf`, {
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function getControlProtocolsApi(params?: {
  page: number;
  size: number;
}): Promise<ProtocolsListResponse> {
  const { data } = await api.get<ProtocolsListResponse>(`/api/files`, {
    params,
  });
  return data;
}

export async function updateProtocolsApi(
  id: number,
  params: Partial<ProtocolsUpdateProps>,
): Promise<ProtocolsListProps> {
  const { data } = await api.patch<ProtocolsListProps>(`/api/files/${id}`, {
    ...params,
  });
  return data;
}

export async function getFileUrlApi(id: number): Promise<{
  presigned_url: string;
}> {
  const { data } = await api.get<{
    presigned_url: string;
  }>(`/api/files/${id}/download`);
  return data;
}

export async function fetchPackingListApi(params: {
  entity_id: number;
  entity: string;
  page: number;
  size: number;
}): Promise<PackingPropsResponse> {
  const { data } = await api.get<PackingPropsResponse>(
    `api/packing-lists/unsafe-to-delete`,
    { params },
  );
  return data;
}
